import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { apiPostBlob } from "@/lib/apiClient";
import { useMsal } from "@azure/msal-react";
import toast from "react-hot-toast";

interface GRCExportModalProps {
  month: number;
  year: number;
  onClose: () => void;
}

const OPTIONS = [
  { id: "current-view", label: "Export Current View" },
  { id: "soft-holds-range", label: "Export Soft Holds by Date Range" }
] as const;

type OptionId = (typeof OPTIONS)[number]["id"];

/**
 * Export modal – mirrors Blazor DownloadGridToExcel / ExecuteExport.
 * Current View → POST /DownloadGRCReporttoExcel/{month}/{year}
 * Soft Holds Range → POST /DownloadSoftholdsReportToExcel/{start}/{end}/{email}
 */
export function GRCExportModal({ month, year, onClose }: GRCExportModalProps) {
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const [selected, setSelected] = useState<OptionId | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startErr, setStartErr] = useState<string | null>(null);
  const [endErr, setEndErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function validateRange(): boolean {
    let valid = true;
    if (selected === "soft-holds-range") {
      if (!startDate) { setStartErr("Start date is required."); valid = false; }
      else setStartErr(null);
      if (!endDate) { setEndErr("End date is required."); valid = false; }
      else if (endDate < startDate) { setEndErr("End date must be on or after start date."); valid = false; }
      else setEndErr(null);
    }
    return valid;
  }

  async function handleExport() {
    if (!selected) {
      toast.error("Please select an export option.");
      return;
    }
    if (!validateRange()) return;

    setLoading(true);
    try {
      let url: string;
      if (selected === "current-view") {
        url = `DownloadGRCReporttoExcel/${month}/${year}`;
        const blob = await apiPostBlob(url, {});
        downloadBlob(blob, `GRC_${year}_${String(month).padStart(2, "0")}.xlsx`);
      } else {
        url = `DownloadSoftholdsReportToExcel/${startDate}/${endDate}/${encodeURIComponent(userEmail)}`;
        const blob = await apiPostBlob(url);
        downloadBlob(blob, `SoftHolds_${startDate}_${endDate}.xlsx`);
      }
      onClose();
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      <div
        className="w-96 rounded-none border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="export-title" className="text-sm font-semibold">Export GRC Report</h2>
          <button type="button" onClick={onClose} aria-label="Close export dialog">
            <X className="h-4 w-4 text-slate-500 hover:text-slate-900" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          {OPTIONS.map((opt) => (
            <label key={opt.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="export-option"
                value={opt.id}
                checked={selected === opt.id}
                onChange={() => { setSelected(opt.id); setStartErr(null); setEndErr(null); }}
              />
              {opt.label}
            </label>
          ))}

          {selected === "soft-holds-range" && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  Start Date <span aria-hidden="true" className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setStartErr(null); }}
                  className="w-full rounded-none border border-slate-300 px-2 py-1 text-sm"
                  aria-describedby={startErr ? "export-start-err" : undefined}
                />
                {startErr && <p id="export-start-err" className="mt-1 text-xs text-red-600">{startErr}</p>}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  End Date <span aria-hidden="true" className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => { setEndDate(e.target.value); setEndErr(null); }}
                  className="w-full rounded-none border border-slate-300 px-2 py-1 text-sm"
                  aria-describedby={endErr ? "export-end-err" : undefined}
                />
                {endErr && <p id="export-end-err" className="mt-1 text-xs text-red-600">{endErr}</p>}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Export
          </button>
        </div>
      </div>
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
